/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { Provider } from 'react-redux';
import { Router, Route, Switch } from 'react-router';

import { configureStore } from './store/configureStore';
import { App } from './components/App/App';
import { TasksPage } from './pages/TasksPage/TasksPage';
import { ParticipantSigningRoomPage } from './pages/SigningRoomPage/ParticipantSigningRoomPage';
import { NotarySigningRoomPage } from './pages/SigningRoomPage/NotarySigningRoomPage';
import { DocumentManagementPage } from './pages/DocumentManagementPage/DocumentManagementPage';
import { NotaryRoomCompletePage } from './pages/NotaryRoomCompletePage/NotaryRoomCompletePage';
import { ParticipantRoomCompletePage } from './pages/ParticipantRoomCompletePage/ParticipantRoomCompletePage';
import { ParticipantRoomEarlyPage } from './pages/ParticipantRoomEarlyPage/ParticipantRoomEarlyPage';
import { ParticipantRoomNotScheduledPage } from './pages/ParticipantRoomNotScheduledPage/ParticipantRoomNotScheduledPage';
import { RoomExitPage } from './pages/RoomExitPage/RoomExitPage';
import { JournalPage } from './pages/JournalPage/JournalPage';
import { RouterStates } from './definitions/RouterStates';
import { history } from './customHistory';
import { SessionWrapper } from './components/ParticipantApp/SessionWrapper/SessionWrapper';
import { LoginWrapper } from './components/NotaryApp/LoginWrapper/LoginWrapper';
import { ProfileCheckWrapper } from './components/NotaryApp/ProfileCheckWrapper/ProfileCheckWrapper';
import { Assignments } from './pages/Notary/Assignments/Assignments';
import { Rooms } from './pages/Notary/Rooms/Rooms';
import { Room } from './pages/Notary/Room/Room';
import { InvitePage } from './pages/Notary/Onboarding/InvitePage';
import { Redirect } from './components/common/Redirect/Redirect';
import { NotaryRouteWrapper } from './components/NotaryApp/NotaryRouteWrapper/NotaryRouteWrapper';
import { StandaloneDeviceTesting } from './components/StandaloneDeviceTesting/StandaloneDeviceTesting';

const REDUX_STORE = configureStore();

const UNKNOWN_PATH_REDIRECT = 'https://blend.com/products/mortgage-suite/close/';

// This configuration assumes a global wrapping "App" components, with content loaded based on the
// route in question. For route configuration questions, refer to
// https://github.com/remix-run/react-router/blob/main/docs/getting-started/tutorial.md
const routeConfiguration = (
  <Router history={history}>
    <Provider store={REDUX_STORE}>
      <App>
        <Switch>
          <Route path={RouterStates.ROOM}>
            <Switch>
              <Route exact path={RouterStates.PARTICIPANT_ROOM_EARLY} component={ParticipantRoomEarlyPage} />
              <Route
                exact
                path={RouterStates.PARTICIPANT_ROOM_NOT_SCHEDULED}
                component={ParticipantRoomNotScheduledPage}
              />
              <Route exact path={RouterStates.PARTICIPANT_ROOM_FINISH} component={ParticipantRoomCompletePage} />
              <Route exact path={RouterStates.ROOM_EXIT} component={RoomExitPage} />
              <Route exact path={RouterStates.DEVICE_TESTING} component={StandaloneDeviceTesting} />

              <SessionWrapper>
                <Switch>
                  <Route exact path={RouterStates.ROOM} component={TasksPage} />
                  <Route exact path={RouterStates.PARTICIPANT_SIGNING_ROOM} component={ParticipantSigningRoomPage} />
                  <Route component={() => <Redirect to={UNKNOWN_PATH_REDIRECT} />} />
                </Switch>
              </SessionWrapper>
            </Switch>
          </Route>

          <Route path={RouterStates.NOTARY}>
            <LoginWrapper>
              <Switch>
                <NotaryRouteWrapper>
                  <Route exact path={RouterStates.NOTARY_ROOM_FINISH} component={NotaryRoomCompletePage} />
                  <Route exact path={RouterStates.ROOMS} component={Rooms} />
                  <Route exact path={RouterStates.ROOM_ASSIGNMENTS} component={Assignments} />
                  <Route exact path={RouterStates.INVITE} component={InvitePage} />
                  <Route exact path={RouterStates.NOTARY_ROOM} component={Room} />
                  <Route exact path={RouterStates.DOCUMENT_MANAGEMENT} component={DocumentManagementPage} />
                  <Route exact path={RouterStates.NOTARY_SIGNING_ROOM}>
                    <ProfileCheckWrapper>
                      <NotarySigningRoomPage />
                    </ProfileCheckWrapper>
                  </Route>
                  <Route exact path={RouterStates.JOURNAL}>
                    <ProfileCheckWrapper>
                      <JournalPage />
                    </ProfileCheckWrapper>
                  </Route>
                  <Route exact path={RouterStates.JOURNAL_ENTRY}>
                    <ProfileCheckWrapper>
                      <JournalPage />
                    </ProfileCheckWrapper>
                  </Route>
                </NotaryRouteWrapper>
                <Route component={() => <Redirect to={UNKNOWN_PATH_REDIRECT} />} />
              </Switch>
            </LoginWrapper>
          </Route>

          <Route component={() => <Redirect to={UNKNOWN_PATH_REDIRECT} />} />
        </Switch>
      </App>
    </Provider>
  </Router>
);

export default routeConfiguration;
